import { useEffect, useRef } from 'react';

/**
 * Native-only stand-in for the global top NavBar on non-dashboard pages.
 *
 * In the native (Capacitor) app we hide the full NavBar on every page except the
 * dashboard for a cleaner, bottom-tab-driven shell. But the NavBar was quietly
 * doing two jobs we must preserve:
 *   1. Pushing page content below the iOS status bar / dynamic island via
 *      env(safe-area-inset-top). This spacer reserves that same inset in-flow.
 *   2. Publishing the global `--navbar-height` CSS var that ~47 pages read for
 *      layout math (min-height calc, fixed-header top offsets). We republish it
 *      as the measured inset height so those calcs collapse the old ~125px gap
 *      while keeping the safe-area clearance.
 *
 * It also tags <body> with `native-no-topnav` so the rule below can re-anchor
 * each page's fixed `top: 0` scroll-compact header to sit below the status bar
 * instead of underneath it. The rule is inert on web (class never added there)
 * and on static elements (top has no effect without positioning).
 */
export default function NativeTopSpacer({ background = '#F7F3EB' }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const publishHeight = () => {
      document.documentElement.style.setProperty('--navbar-height', `${el.offsetHeight}px`);
    };
    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(el);
    document.body.classList.add('native-no-topnav');

    return () => {
      observer.disconnect();
      document.body.classList.remove('native-no-topnav');
    };
  }, []);

  return (
    <>
      <style>{`
        body.native-no-topnav .latest-sticky-header .latest-header-compact,
        body.native-no-topnav .wlm-sticky-header .wlm-compact-bar,
        body.native-no-topnav .search-sticky-header .search-compact-bar,
        body.native-no-topnav .rec-sticky-header .rec-compact-bar,
        body.native-no-topnav .oh-sticky-header .oh-compact-bar,
        body.native-no-topnav .op-sticky-header .op-compact-bar,
        body.native-no-topnav .cp-sticky-header .cp-compact-bar,
        body.native-no-topnav .ap-sticky-header .ap-compact-bar,
        body.native-no-topnav .ct-sticky-header .ct-compact-bar,
        body.native-no-topnav .at-sticky-header .at-compact-bar,
        body.native-no-topnav .tc-sticky-header .tc-compact-bar,
        body.native-no-topnav .matches-sticky-header .matches-compact-bar,
        body.native-no-topnav .my-pedals-sticky-header .my-pedals-compact-bar,
        body.native-no-topnav .add-pedal-sticky-header .add-pedal-compact-bar {
          top: env(safe-area-inset-top) !important;
        }

        /* Content surfaces carry top margin/padding originally sized as breathing
           room below the old ~125px navbar. With the nav hidden on native, that
           reads as an awkward empty strip above the content. Trim it so content
           sits snug under the safe-area strip, matching the Trading hub (~1rem). */
        body.native-no-topnav .pedal-info-box {
          margin-top: 1rem !important;
        }
        body.native-no-topnav .latest-pedals-grid-section {
          padding-top: 1rem !important;
        }
      `}</style>
      {/* Fixed cream bar that always covers the iOS status-bar / dynamic-island
          strip. The in-flow spacer below scrolls away with the page, so once
          scrolled, page content (and any fixed compact header pinned just below
          the inset) would otherwise show through under the status bar. This stays
          put. Sits above page content + compact headers but below full-screen
          modals/sheets (which draw their own status-bar area). */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 'env(safe-area-inset-top)',
          backgroundColor: background,
          zIndex: 1003,
          pointerEvents: 'none',
        }}
      />
      {/* In-flow spacer: reserves the inset on first paint and publishes the
          measured height into --navbar-height for the ~47 pages that read it. */}
      <div
        ref={ref}
        aria-hidden="true"
        style={{
          height: 'env(safe-area-inset-top)',
          backgroundColor: background,
          flexShrink: 0,
        }}
      />
    </>
  );
}
